import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Modal } from '@/components';
import { Alert } from '@/design-system/alert';
import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';

import { parseWarehouseError, warehouseApi } from '../api/warehouse.api';
import { downloadCsv, readCsvFile } from '../format';
import { rowErrorsOf } from '../hooks/useServerErrors';
import {
  type ParsedSkuRow,
  parseSkuCsv,
  SKU_CSV_HEADERS,
  SKU_STATUS_LABEL,
  skuPropLabel,
  skuRowErrors,
} from '../skuMaster';
import type {
  ClientRef,
  FieldError,
  ImportSkusResult,
  SkuStatus,
} from '../types';
import { CsvErrorTable, CsvFileProblem } from './CsvErrorTable';
import {
  describeUploadError,
  localFileProblem,
  type PickError,
} from './csvImportErrors';
import { DisabledReason } from './dialogs';
import { FormField } from './form';
import { ClientSelect } from './pickers';
import { codeCls, metaCls, nameCls } from './ui';

const errorText = (errors: FieldError[]) =>
  errors
    .map((e) => {
      const label = skuPropLabel(e.field);
      return e.message.startsWith(label) ? e.message : `${label}: ${e.message}`;
    })
    .join(' · ');

const COLUMNS: TableDataColumn<ParsedSkuRow>[] = [
  {
    key: 'line',
    title: 'Baris',
    render: (_v, r) => <span className="tabular-nums">{r.line}</span>,
  },
  {
    key: 'sku',
    title: 'SKU Code',
    render: (_v, r) => <span className={codeCls}>{r.sku || '—'}</span>,
  },
  {
    key: 'itemName',
    title: 'SKU Name',
    render: (_v, r) => <span className={nameCls}>{r.itemName || '—'}</span>,
  },
  { key: 'uomName', title: 'UOM' },
  {
    key: 'status',
    title: 'Status',
    render: (_v, r) => (
      <span className={metaCls}>
        {SKU_STATUS_LABEL[r.status as SkuStatus] ?? r.status}
      </span>
    ),
  },
  {
    key: 'check',
    title: 'Cek',
    render: (_v, r) =>
      r.errors.length ? (
        <span
          className="text-[12px] text-(--state-error-dark)"
          data-testid={`sku-row-error-${r.line}`}
        >
          {errorText(r.errors)}
        </span>
      ) : (
        <div className="flex flex-col items-start gap-1">
          <Badge status="success" size="sm" type="dot">
            OK
          </Badge>
          {r.hints.length > 0 && (
            <span
              className="text-[11px] text-(--state-warning-dark)"
              data-testid={`sku-row-hint-${r.line}`}
            >
              {r.hints.join(' ')}
            </span>
          )}
        </div>
      ),
  },
];

/**
 * Import SKUs from ops' master_sku sheet exactly as exported: blank padding
 * rows / columns, the header found by "SKU Code", headers matched ignoring
 * case and spaces, Yes/No and Active/Non Active. Every row must be valid (the
 * server rejects the batch otherwise); existing SKU codes are skipped.
 * Packaging mismatches are only a hint.
 */
export const SkuImportModal: React.FC<{
  open: boolean;
  initialClient: ClientRef | null;
  onClose: () => void;
  onImported: (res: ImportSkusResult) => void;
}> = ({ open, initialClient, onClose, onImported }) => {
  const [client, setClient] = useState<ClientRef | null>(initialClient);
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<ParsedSkuRow[]>([]);
  const [ignored, setIgnored] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<PickError | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setClient(initialClient);
    setFileName(null);
    setRows([]);
    setIgnored([]);
    setError(null);
    setBusy(false);
  }, [open, initialClient]);

  const invalid = useMemo(
    () => rows.filter((r) => r.errors.length).length,
    [rows]
  );
  const rowErrors = useMemo(() => skuRowErrors(rows), [rows]);
  const hinted = useMemo(
    () => rows.filter((r) => r.hints.length).length,
    [rows]
  );

  if (!open) return null;

  const readFile = async (f: File | null) => {
    setError(null);
    if (!f) return;
    const read = await readCsvFile(f);
    if (read.error !== null) {
      setError(
        localFileProblem(
          read.error,
          'Simpan sheet sebagai .csv, atau unduh template lalu salin datanya ke sana.'
        )
      );
      return;
    }
    const parsed = parseSkuCsv(read.text);
    if (!parsed.rows) {
      setError(
        localFileProblem(
          'Header "SKU Code" tidak ditemukan — unduh template untuk format yang benar.',
          'Baris header harus punya kolom "SKU Code". Salin data ke template (nama kolom di baris pertama), lalu pilih ulang file.'
        )
      );
      return;
    }
    if (parsed.missingRequired.length) {
      setError(
        localFileProblem(
          `Kolom wajib tidak ada: ${parsed.missingRequired.join(', ')}.`,
          'Tambahkan kolom itu di baris header (nama persis seperti template), lalu pilih ulang file.'
        )
      );
      return;
    }
    if (!parsed.rows.length) {
      setError(
        localFileProblem(
          'File tidak punya baris data.',
          'Isi minimal satu baris SKU di bawah header, lalu pilih ulang file.'
        )
      );
      return;
    }
    setFileName(f.name);
    setRows(parsed.rows);
    setIgnored(parsed.ignored);
  };

  const submit = async () => {
    // Every row validated → every row has its payload (typed, no assertion).
    const payload = rows.flatMap((r) => (r.value ? [r.value] : []));
    if (!client || !rows.length || invalid || payload.length !== rows.length)
      return;
    setBusy(true);
    setError(null);
    try {
      onImported(
        await warehouseApi.importSkus({
          clientID: client.id,
          clientName: client.name,
          rows: payload,
        })
      );
    } catch (err) {
      // A 422 names its rows (rows[i].<field>): show them on the preview.
      const byRow = rowErrorsOf(err);
      if (byRow.size) {
        setRows((prev) =>
          prev.map((r, i) =>
            byRow.has(i) ? { ...r, value: null, errors: byRow.get(i) ?? [] } : r
          )
        );
      } else
        setError(
          describeUploadError(parseWarehouseError(err), {
            fileHint:
              'Unduh template terbaru, salin data ke sana, lalu pilih ulang file.',
            requestTitle: 'Import gagal',
            requestAction: 'Import',
          })
        );
    } finally {
      setBusy(false);
    }
  };

  /** Why Import is off (visible next to it). */
  const blocked = !client
    ? 'Pilih client'
    : !rows.length
      ? 'Pilih file CSV'
      : invalid > 0
        ? `${invalid} baris error — perbaiki dulu`
        : null;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      icon="ri-upload-2-line"
      title="Import SKU (CSV)"
      width="xl"
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      footer={
        <div className="flex items-center justify-end gap-2">
          {blocked && (
            <DisabledReason testId="sku-import-reason" className="mr-auto">
              {blocked}
            </DisabledReason>
          )}
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={busy}
          >
            Batal
          </Button>
          <Button
            onClick={() => void submit()}
            loading={busy}
            disabled={!!blocked}
            data-testid="sku-import-submit"
          >
            {rows.length && !invalid ? `Import ${rows.length} SKU` : 'Import'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <FormField label="Client" required>
          <ClientSelect value={client} onChange={setClient} />
        </FormField>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            data-testid="sku-import-file"
            onChange={(e) => {
              void readFile(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          <Button
            tone="neutral"
            style="stroke"
            onClick={() => inputRef.current?.click()}
            leftIcon={<i className="ri-file-upload-line text-[14px]" />}
          >
            {fileName ? 'Ganti file' : 'Pilih file CSV'}
          </Button>
          {fileName && <span className={metaCls}>{fileName}</span>}
          <span className="flex-1" />
          <Button
            tone="primary"
            style="link"
            size="xs"
            className="text-xs font-semibold"
            onClick={() => downloadCsv('template-sku.csv', [SKU_CSV_HEADERS])}
            leftIcon={
              <i className="ri-download-2-line text-[14px]" aria-hidden />
            }
          >
            Download template
          </Button>
        </div>
        {error && <CsvFileProblem problem={error} testId="sku-import-error" />}
        {invalid > 0 && (
          <Alert
            status="warning"
            appearance="stroke"
            size="sm"
            title={`${invalid} baris belum valid — perbaiki file lalu pilih ulang.`}
            data-testid="sku-import-invalid"
          />
        )}
        {hinted > 0 && !invalid && (
          <Alert
            status="information"
            appearance="stroke"
            size="sm"
            title={`${hinted} baris punya angka kemasan yang tidak konsisten`}
          />
        )}
        {ignored.length > 0 && (
          <p className={metaCls}>Kolom diabaikan: {ignored.join(', ')}.</p>
        )}
        {rowErrors.length > 0 && (
          <CsvErrorTable
            errors={rowErrors}
            fileName={fileName}
            maxHeight={260}
          />
        )}
        {rows.length > 0 && (
          <div className="max-h-[360px] overflow-auto">
            <TableData size="sm" data={rows} columns={COLUMNS} />
          </div>
        )}
      </div>
    </Modal>
  );
};
